/**
 * Purpose: Reusable search/filter/sort row for module collections; renders only
 *          the controls the caller wires up (opt-in per module).
 * Inputs: filters (the object returned by useCollectionFilters), plus flags:
 *         withSearch, withCategory, withAvailability, withSort, searchPlaceholder.
 * Outputs: responsive control row using brand tokens; announces result count.
 */
import React from 'react';

const controlClasses =
  'rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200';

const formatCategoryLabel = (value) =>
  String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const FilterBar = ({
  filters,
  withSearch = true,
  withCategory = false,
  withAvailability = false,
  withSort = true,
  searchPlaceholder = 'Search…',
  className = '',
}) => {
  const {
    query,
    setQuery,
    category,
    setCategory,
    categories,
    availabilityOnly,
    setAvailabilityOnly,
    sort,
    setSort,
    sortOptions,
    filteredCount,
    totalCount,
    isFiltering,
  } = filters;

  return (
    <div className={`mb-4 space-y-2 ${className}`}>
      <div className="flex flex-wrap items-center gap-2">
        {withSearch && (
          <label className="relative min-w-0 flex-1 basis-48">
            <span className="sr-only">Search</span>
            <svg
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className={`${controlClasses} w-full pl-9`}
            />
          </label>
        )}

        {withCategory && categories.length > 1 && (
          <label>
            <span className="sr-only">Filter by category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={controlClasses}>
              <option value="">All categories</option>
              {categories.map((value) => (
                <option key={value} value={value}>
                  {formatCategoryLabel(value)}
                </option>
              ))}
            </select>
          </label>
        )}

        {withSort && (
          <label>
            <span className="sr-only">Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className={controlClasses}>
              {sortOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {withAvailability && (
          <button
            type="button"
            onClick={() => setAvailabilityOnly(!availabilityOnly)}
            aria-pressed={availabilityOnly}
            className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              availabilityOnly
                ? 'bg-brand-600 text-white'
                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Available only
          </button>
        )}
      </div>

      {isFiltering && (
        <p className="text-xs text-gray-500 dark:text-gray-400" role="status">
          Showing {filteredCount} of {totalCount}
        </p>
      )}
    </div>
  );
};

export default FilterBar;
